/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import type { RendererProps } from './types'

import type { NormalizedChange } from '../diff/normalize'
import type { FieldRow } from '../primitives'
import { FieldTable, NoChanges, Section } from '../primitives'

/**
 * The create and delete layouts.
 *
 * A create has no meaningful "before" and a delete no meaningful "after". Forcing either into a
 * before/after layout produces a column of empty boxes that implies something was lost, so both
 * render as a structured read-only card of the values instead.
 *
 * A create diff embeds the entire entity, so nested objects are given their own section rather
 * than being flattened into one enormous list.
 */

/** Split changes into top-level scalar rows and nested objects worth their own section. */
const partition = (changes: NormalizedChange[], pick: (c: NormalizedChange) => unknown) => {
  const rows: FieldRow[] = []
  const groups = new Map<string, FieldRow[]>()

  for (const change of changes) {
    const value = pick(change)
    const row: FieldRow = { label: change.label, value, title: change.path }

    if (change.segments.length > 1) {
      // `alertNotificationMedia.red.email` groups under "Alert notification media".
      const [root] = change.segments
      const existing = groups.get(root)
      if (existing) existing.push(row)
      else groups.set(root, [row])
    } else {
      rows.push(row)
    }
  }

  return { rows, groups }
}

const Snapshot: React.FC<{
  changes: NormalizedChange[]
  pick: (c: NormalizedChange) => unknown
  heading: string
  tone: 'success' | 'error'
}> = ({ changes, pick, heading, tone }) => {
  if (changes.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <NoChanges message="No values were recorded for this change." />
      </div>
    )
  }

  const { rows, groups } = partition(changes, pick)
  // A create whose whole entity sits under one root would otherwise show an empty top section.
  const labelFor = (root: string) => changes.find((c) => c.segments[0] === root)?.label ?? root

  return (
    <div className="flex flex-col gap-3">
      <Section title={heading} tone={tone} count={changes.length}>
        {rows.length > 0 ? <FieldTable rows={rows} /> : <p className="text-sm text-base-content/60">See below.</p>}
      </Section>

      {Array.from(groups.entries()).map(([root, groupRows]) => (
        <Section key={root} title={labelFor(root)} count={groupRows.length} defaultOpen={groups.size <= 3}>
          <FieldTable rows={groupRows} />
        </Section>
      ))}
    </div>
  )
}

/** The resulting values of a newly created entity. */
export const CreateRenderer: React.FC<RendererProps> = ({ changes }) => (
  <Snapshot changes={changes} pick={(c) => c.after ?? c.before} heading="Created with these values" tone="success" />
)

/** The last known state of a deleted entity. */
export const DeleteRenderer: React.FC<RendererProps> = ({ changes }) => (
  <Snapshot changes={changes} pick={(c) => c.before ?? c.after} heading="Deleted — last known values" tone="error" />
)
